import { ReactElement } from "react";

export const HTTP_CODES = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  NOT_MODIFIED: 304,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const satisfies Record<string, number>;

export type ServerError = Record<string, unknown> & {
  location?: string;
  name?: string;
  description: string;
  error_code?: string;
  transKey?: string;
  transValues?: Record<string, string>;
};

export type ServerErrorResponse = {
  status?: "error";
  errors: ServerError[];
};

export type TalaeraError =
  | ServerErrorResponse
  | ServerError
  | Array<ServerError>
  | ReactElement
  | string;

export class ApiRequestError extends Error {
  response: Response | undefined;
  responseBody: unknown;

  constructor(
    response: Response | undefined,
    responseBody: unknown,
    originalError?: Error
  ) {
    super();
    this.response = response;
    this.responseBody = responseBody;
    this.cause = originalError;
  }

  get errors(): ServerError[] | undefined {
    return isServerErrorResponse(this.responseBody)
      ? this.responseBody.errors
      : undefined;
  }

  get isUnauthorized() {
    return (
      this.response?.status === HTTP_CODES.UNAUTHORIZED ||
      this.response?.status === HTTP_CODES.FORBIDDEN
    );
  }

  get message() {
    if (navigator.onLine === false) {
      return ApiRequestError.OFFLINE_ERROR;
    }

    if (this.isUnauthorized) {
      return ApiRequestError.AUTH_ERROR;
    }

    if (this.response && this.response.status >= HTTP_CODES.INTERNAL_SERVER_ERROR) {
      return ApiRequestError.SERVER_ERROR;
    }

    if (isServerErrorResponse(this.responseBody)) {
      return this.responseBody.errors
        .map(e => e.description)
        .filter(Boolean)
        .join(" ");
    }

    return this.cause instanceof Error
      ? this.cause.message
      : "An unknown error occurred.";
  }

  toString() {
    return this.message;
  }

  static SERVER_ERROR =
    "We were unable to perform your request, and our engineers were notified. In the meantime, please try again.";
  static OFFLINE_ERROR =
    "Oops, network issues appear to be interfering with your request. Please change networks or try again later";
  static AUTH_ERROR =
    "Your session has expired or you are not allowed to perform these actions. Please try to log in again.";
}

export function isServerErrorResponse(body: unknown): body is ServerErrorResponse {
  return (
    !!body &&
    typeof body === "object" &&
    "errors" in body &&
    (body as { status?: unknown }).status === "error"
  );
}
